// Types for Compliance and Audit System (INDRA)

export type ActionCategory =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'sign'
  | 'export'
  | 'login'
  | 'logout'
  | 'config';

export type SensitivityLevel = 'low' | 'normal' | 'high' | 'critical';

export type AlertSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';

export type AlertStatus =
  | 'open'
  | 'acknowledged'
  | 'investigating'
  | 'resolved'
  | 'false_positive'
  | 'escalated';

export type AlertType =
  | 'policy_violation'
  | 'integrity_failure'
  | 'unauthorized_access'
  | 'anomaly_detected'
  | 'signature_expired'
  | 'certificate_expiring'
  | 'segregation_violation'
  | 'data_breach'
  | 'access_denied'
  | 'bulk_operation'
  | 'config_change'
  | 'escalation_required';

export interface AuditLogRecord {
  id: string;
  sequence_number: number;
  entity_type: string;
  entity_id: string;
  action: string;
  action_category: ActionCategory;
  previous_state: Record<string, any> | null;
  new_state: Record<string, any> | null;
  change_summary: string | null;
  performed_by: string | null;
  performed_by_role: string;
  performed_by_name: string;
  ip_address: string | null;
  user_agent: string | null;
  session_id: string | null;
  previous_hash: string;
  record_hash: string;
  timestamp_utc: string;
  sensitivity_level: SensitivityLevel;
  compliance_tags: string[];
  created_at: string;
}

export interface ComplianceAlert {
  id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  category: string;
  title: string;
  description: string;
  recommendation: string | null;
  entity_type: string | null;
  entity_id: string | null;
  audit_log_id: string | null;
  status: AlertStatus;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  assigned_to: string | null;
  detection_source: string;
  detection_rule: string | null;
  risk_score: number;
  affected_standards: string[];
  detected_at: string;
  due_date: string | null;
  sla_breach: boolean;
  created_at: string;
  updated_at: string;
}

export interface ComplianceConfig {
  id: string;
  config_key: string;
  config_value: Record<string, any>;
  description: string | null;
  category: string | null;
  is_sensitive: boolean;
  last_modified_by: string | null;
  last_modified_at: string;
  created_at: string;
  updated_at: string;
}

export interface ComplianceMetrics {
  period_days: number;
  generated_at: string;
  audit_metrics: {
    total_records: number;
    high_risk_actions: number;
    by_category: Record<string, number>;
    by_sensitivity: Record<string, number>;
  };
  alert_metrics: {
    total_alerts: number;
    open_alerts: number;
    critical_open: number;
    sla_breaches: number;
  };
  integrity_status: {
    chain_valid: boolean;
    records_checked: number;
    last_check: string;
  };
}

export interface IntegrityCheckResult {
  is_valid: boolean;
  checked_records: number;
  broken_at_sequence: number | null;
  error_details: string | null;
}

// Labels en espanol
export const actionCategoryLabels: Record<ActionCategory, string> = {
  create: 'Crear',
  read: 'Leer',
  update: 'Actualizar',
  delete: 'Eliminar',
  approve: 'Aprobar',
  reject: 'Rechazar',
  sign: 'Firmar',
  export: 'Exportar',
  login: 'Iniciar sesion',
  logout: 'Cerrar sesion',
  config: 'Configurar',
};

export const sensitivityLabels: Record<SensitivityLevel, string> = {
  low: 'Bajo',
  normal: 'Normal',
  high: 'Alto',
  critical: 'Critico',
};

export const alertSeverityLabels: Record<AlertSeverity, string> = {
  info: 'Informativo',
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
  critical: 'Critico',
};

export const alertStatusLabels: Record<AlertStatus, string> = {
  open: 'Abierta',
  acknowledged: 'Reconocida',
  investigating: 'Investigando',
  resolved: 'Resuelta',
  false_positive: 'Falso positivo',
  escalated: 'Escalada',
};

// Colores para badges (Tailwind)
export const sensitivityColors: Record<SensitivityLevel, string> = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export const alertSeverityColors: Record<AlertSeverity, string> = {
  info: 'bg-gray-100 text-gray-800',
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export const alertStatusColors: Record<AlertStatus, string> = {
  open: 'bg-red-100 text-red-800',
  acknowledged: 'bg-yellow-100 text-yellow-800',
  investigating: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  false_positive: 'bg-gray-100 text-gray-800',
  escalated: 'bg-purple-100 text-purple-800',
};

export const actionCategoryColors: Record<ActionCategory, string> = {
  create: 'bg-green-100 text-green-800',
  read: 'bg-gray-100 text-gray-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-orange-100 text-orange-800',
  sign: 'bg-purple-100 text-purple-800',
  export: 'bg-cyan-100 text-cyan-800',
  login: 'bg-indigo-100 text-indigo-800',
  logout: 'bg-slate-100 text-slate-800',
  config: 'bg-amber-100 text-amber-800',
};
